import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import API_BASE_URL from './config';
import { downloadFromApi } from './desktop/downloadFromApi';
import './PersonGallery.css';
import {
  buildChunksByOrientation,
  pickCaptions,
  applyCustomOrder,
} from './magazineLayout';
import useImageRatios from './useImageRatios';
import GalleryGrid from './GalleryGrid';
import StudioBrandBar from './StudioBrandBar';
import { themeOf, isGridLayout, themeClass } from './galleryThemes';

const pluralize = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const buildTitle = (cluster) => {
  const count = cluster.photo_count;
  if (cluster.label) return `${pluralize(count, 'Moment')} with ${cluster.label}`;
  return `A Collection of ${pluralize(count, 'Moment')}`;
};

const buildDescription = (event) => {
  const parts = [];
  if (event.event_name) parts.push(`From ${event.event_name}`);
  else if (event.groom_name && event.bridal_name) parts.push(`From ${event.groom_name} & ${event.bridal_name}'s celebration`);
  if (event.event_date) {
    parts.push(new Date(event.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
  }
  return parts.join(' · ');
};


function PersonGallery() {
  const { token, clusterId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [downloading, setDownloading] = useState(false);

  const [canEdit, setCanEdit] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editOrder, setEditOrder] = useState([]);
  const [editCaptions, setEditCaptions] = useState([]);
  const [selectedTileId, setSelectedTileId] = useState(null);
  const [dragSrcId, setDragSrcId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [saving, setSaving] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    // Same localStorage key ClientGallery uses — so if this viewer unselected a
    // photo in the main gallery on this device, it stays hidden here too.
    const clientId = localStorage.getItem("cg_clientId") || "client_" + Date.now();
    localStorage.setItem("cg_clientId", clientId);

    const fetchPage = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/shared/${token}/people/${clusterId}/page?clientId=${clientId}`);
        if (!res.ok) {
          setError(res.status === 404 ? 'This gallery could not be found.' : 'Something went wrong loading this gallery.');
          setLoading(false);
          return;
        }
        setData(await res.json());
      } catch (e) {
        setError('Something went wrong loading this gallery.');
      }
      setLoading(false);
    };
    fetchPage();
  }, [token, clusterId]);

  // Edit controls only appear for the studio that owns the event. The page is
  // public, so this asks the server whether the login token in this browser
  // (if any) belongs to the event's owner — the save endpoint enforces the
  // same check, this just hides dead controls from guests.
  useEffect(() => {
    if (!data?.event?.id) return;
    const authToken = localStorage.getItem('token');
    if (!authToken) return;
    fetch(`${API_BASE_URL}/api/events/${data.event.id}/people/edit-check`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then((d) => setCanEdit(!!d.canEdit))
      .catch(() => {});
  }, [data]);

  // A photo cannot be given a slot before its shape is known, so how much has
  // been measured is also how much is on the page.
  const allPhotos = data?.photos;
  const { ratios, measured, measureMore, hasMore } = useImageRatios(allPhotos);

  // The base (read-only) view — reshuffled only when the fetched data
  // changes, never on unrelated re-renders like opening the lightbox.
  const viewSpread = useMemo(() => {
    if (!data) return null;
    const ordered = applyCustomOrder(data.photos, data.customLayout?.photoOrder);
    const ready = ordered.slice(0, measured);
    const chunks = buildChunksByOrientation(ready, ratios);
    const captions = pickCaptions(chunks.length, data.customLayout?.captions);
    return { chunks, captions, orderedPhotos: ordered };
  }, [data, measured, ratios]);

  // Photo lookup by id, used to rebuild chunks from editOrder while editing.
  const photosById = useMemo(() => {
    if (!data) return new Map();
    return new Map(data.photos.map((p) => [p.id, p]));
  }, [data]);

  const editSpreadChunks = useMemo(() => {
    if (!editMode) return null;
    const orderedPhotos = editOrder.map((id) => photosById.get(id)).filter(Boolean);
    return buildChunksByOrientation(orderedPhotos, ratios);
  }, [editMode, editOrder, photosById, ratios]);

  const spread = editMode
    ? { chunks: editSpreadChunks, captions: editCaptions }
    : viewSpread;

  const shownChunks = spread?.chunks || [];
  // The grid pages on the same measured prefix, and through the studio's saved
  // order, so both themes show the same photos in the same sequence.
  const readyPhotos = useMemo(
    () => (viewSpread?.orderedPhotos || []).slice(0, measured),
    [viewSpread, measured]
  );
  // Editing needs every tile on the page at once — you cannot drag a photo into
  // a group that has not rendered — so entering edit mode measures the rest.
  const hasMoreChunks = !editMode && hasMore;

  useEffect(() => {
    if (editMode && hasMore) measureMore();
  }, [editMode, hasMore, measureMore]);

  // Auto-advance on scroll; the button below is the fallback for browsers
  // without IntersectionObserver and for keyboard users.
  useEffect(() => {
    if (!hasMoreChunks || typeof IntersectionObserver === 'undefined') return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) measureMore(); },
      { rootMargin: '600px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMoreChunks, measureMore]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = buildTitle(data.cluster);
    if (navigator.share) {
      try { await navigator.share({ title, text: buildDescription(data.event), url }); }
      catch (e) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copied to clipboard!');
      } catch (e) {
        showToast('Could not copy link');
      }
    }
  };

  const handleDownloadPhoto = async (e, photo) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE_URL}/api/images/${photo.id}/download`);
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `photo-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      showToast('Download failed — please try again');
    }
  };

  const handleDownloadAll = async () => {
    if (!data || downloading) return;
    setDownloading(true);
    try {
      // Streams to disk on desktop instead of buffering the archive in memory;
      // in a browser it falls back to the blob-and-anchor path.
      await downloadFromApi({
        url: `${API_BASE_URL}/api/events/${data.event.id}/download-zip`,
        filename: `${(data.cluster.label || 'photos').replace(/\s+/g, '-').toLowerCase()}.zip`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageIds: data.photos.map(p => p.id),
          folderName: data.cluster.label || 'photos',
        }),
      });
    } catch (e) {
      showToast('Download failed — please try again');
    }
    setDownloading(false);
  };

  const handleDownloadPdf = () => {
    // No PDF service in this stack — the browser's own print pipeline (choose
    // "Save as PDF" as the destination) renders this exact page, styled by
    // the @media print rules below, into a real PDF file.
    window.print();
  };

  const startEdit = () => {
    setEditOrder(viewSpread.orderedPhotos.map((p) => p.id));
    setEditCaptions([...viewSpread.captions]);
    setSelectedTileId(null);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setSelectedTileId(null);
    setDragSrcId(null);
    setDragOverId(null);
  };

  // Tap-to-select then tap-another-to-swap
  const handleTileClick = (photoId) => {
    if (!editMode) return;
    if (dragSrcId !== null) return;
    if (selectedTileId === null) { setSelectedTileId(photoId); return; }
    if (selectedTileId === photoId) { setSelectedTileId(null); return; }
    setEditOrder((prev) => {
      const next = [...prev];
      const a = next.indexOf(selectedTileId);
      const b = next.indexOf(photoId);
      if (a !== -1 && b !== -1) [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
    setSelectedTileId(null);
  };

  // Desktop drag-and-drop
  const handleDragStart = (e, photoId) => {
    setDragSrcId(photoId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, photoId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (photoId !== dragSrcId) setDragOverId(photoId);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!dragSrcId || dragSrcId === targetId) { setDragSrcId(null); setDragOverId(null); return; }
    setEditOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(dragSrcId);
      const to = next.indexOf(targetId);
      if (from !== -1 && to !== -1) { next.splice(from, 1); next.splice(to, 0, dragSrcId); }
      return next;
    });
    setDragSrcId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => { setDragSrcId(null); setDragOverId(null); };
  const handleDragLeave = () => setDragOverId(null);

  const handleCaptionChange = (idx, value) => {
    setEditCaptions((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const handleSaveLayout = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${data.event.id}/people/${clusterId}/layout`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ photoOrder: editOrder, captions: editCaptions }),
      });
      if (!res.ok) throw new Error('save failed');
      const saved = await res.json();
      setData((prev) => ({ ...prev, customLayout: saved.customLayout }));
      setEditMode(false);
      setSelectedTileId(null);
      showToast('Layout saved — this arrangement is now fixed for everyone.');
    } catch (e) {
      showToast('Could not save — please try again');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="pg-status-page">
        <div className="pg-spinner" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="pg-status-page">
        <h1 className="pg-status-title">Gallery Not Found</h1>
        <p className="pg-status-desc">{error || 'This link may have expired.'}</p>
      </div>
    );
  }

  const { event, cluster, photos, studioName, logoUrl, bannerUrl } = data;
  // An unknown or missing theme falls back to the magazine — that is what
  // every event showed before themes existed.
  const theme = themeOf(event.gallery_theme);
  const isGrid = isGridLayout(event.gallery_theme);
  const title = buildTitle(cluster);
  const description = buildDescription(event);

  return (
    <div className={`pg-page ${themeClass(event.gallery_theme)}${editMode ? ' pg-page--editing' : ''}`}>
      <StudioBrandBar studioName={studioName} logoUrl={logoUrl} bannerUrl={bannerUrl} />
      {/* Hero — a real <img>, not a CSS background, so it always shows up in print/PDF */}
      <div className="pg-hero">
        {/* The hero is the largest image on the page, so it cannot use
            cover_url — that is the 400px grid thumbnail and it looked soft
            blown up across the banner while the tiles below were sharp. The
            photo list is ordered best-closeup-first, so photos[0] is the same
            picture the cover points at, at full resolution. */}
        {(photos[0]?.image_url || cluster.cover_url) && (
          <img className="pg-hero-bg" src={photos[0]?.image_url || cluster.cover_url} alt="" />
        )}
        <div className="pg-hero-scrim" />
        <div className="pg-hero-content">
          <span className="pg-eyebrow">{event.event_name || 'Event Gallery'}</span>
          <h1 className="pg-title">{title}</h1>
          {description && <p className="pg-desc">{description}</p>}
          <div className="pg-actions">
            <button className="pg-btn pg-btn--primary" onClick={handleShare}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path d="M13 4.5a2.5 2.5 0 1 1 .702 1.737L6.97 9.604a2.518 2.518 0 0 1 0 .792l6.733 3.367a2.5 2.5 0 1 1-.671 1.341l-6.733-3.367a2.5 2.5 0 1 1 0-3.474l6.733-3.367A2.52 2.52 0 0 1 13 4.5Z"/></svg>
              Share
            </button>
            <button className="pg-btn" onClick={handleDownloadAll} disabled={downloading}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path d="M10 12.5a.75.75 0 0 0 .53-.22l3.5-3.5a.75.75 0 0 0-1.06-1.06L10.75 9.94V3a.75.75 0 0 0-1.5 0v6.94L7.03 7.72a.75.75 0 0 0-1.06 1.06l3.5 3.5c.14.14.331.22.53.22ZM4.75 13a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 6 18.25h8a2.75 2.75 0 0 0 2.75-2.75V13a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H6c-.69 0-1.25-.56-1.25-1.25V13Z"/></svg>
              {downloading ? 'Preparing…' : 'Download All'}
            </button>
            <button className="pg-btn" onClick={handleDownloadPdf}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path d="M5.5 2A1.5 1.5 0 0 0 4 3.5v13A1.5 1.5 0 0 0 5.5 18h9a1.5 1.5 0 0 0 1.5-1.5V7.621a1.5 1.5 0 0 0-.44-1.06l-4.12-4.122A1.5 1.5 0 0 0 10.378 2H5.5ZM10 3v3.5A1.5 1.5 0 0 0 11.5 8H15v8.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 .5-.5H10Z"/></svg>
              Download PDF
            </button>
            {canEdit && !editMode && (
              <button className="pg-btn pg-btn--edit" onClick={startEdit}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path d="M13.586 3.586a2 2 0 1 1 2.828 2.828l-8.5 8.5a2 2 0 0 1-.878.507l-2.872.82a.5.5 0 0 1-.618-.618l.82-2.872a2 2 0 0 1 .507-.878l8.5-8.5Z"/></svg>
                Customize
              </button>
            )}
          </div>
        </div>
      </div>

      {editMode && (
        <div className="pg-edit-hint">
          {selectedTileId
            ? '✦ Now tap another photo to swap it'
            : 'Tap any photo to select it, then tap another to swap · edit words below'}
        </div>
      )}

      {/* The grid theme drops the editorial rows and the words between them.
          Customising arranges the magazine spread, so it always shows that —
          there is no order or caption to arrange in a plain grid. */}
      {isGrid && !editMode ? (
        <>
          <GalleryGrid photos={readyPhotos} ratios={ratios} onDownload={handleDownloadPhoto} />
          {hasMore && (
            <div className="pg-more">
              <div ref={sentinelRef} className="pg-more-sentinel" aria-hidden="true" />
              <button className="pg-more-btn" onClick={measureMore}>
                Show more photos
              </button>
            </div>
          )}
        </>
      ) : (
      <>
      {/* Editorial spread: photo groups alternating with cherishing words */}
      <div className="pg-spread">
        {shownChunks.map((chunk, ci) => (
          <React.Fragment key={editMode ? `edit-${ci}` : `view-${ci}`}>
            <div className={`pg-chunk pg-chunk--${chunk.layout}`}>
              {chunk.photos.map((photo, li) => (
                <div
                  key={photo.id}
                  draggable={editMode}
                  className={[
                    'pg-chunk-tile',
                    editMode && 'pg-chunk-tile--editable',
                    selectedTileId === photo.id && 'pg-chunk-tile--selected',
                    dragSrcId === photo.id && 'pg-chunk-tile--dragging',
                    dragOverId === photo.id && 'pg-chunk-tile--drag-over',
                  ].filter(Boolean).join(' ')}
                  onClick={() => editMode ? handleTileClick(photo.id) : undefined}
                  onDragStart={editMode ? (e) => handleDragStart(e, photo.id) : undefined}
                  onDragOver={editMode ? (e) => handleDragOver(e, photo.id) : undefined}
                  onDrop={editMode ? (e) => handleDrop(e, photo.id) : undefined}
                  onDragEnd={editMode ? handleDragEnd : undefined}
                  onDragLeave={editMode ? handleDragLeave : undefined}
                >
                  {/* Always the full photo, never the 400px quality-65 grid
                      thumbnail. Offering the thumbnail as an srcset candidate
                      was worse than it sounds: a trio tile is ~380 CSS px, so
                      on a non-retina screen the browser correctly judged 400w
                      "enough" and every guest on a standard display got the
                      soft thumbnail. This page has no lightbox — the tile is
                      the only view of the photo anyone gets. Cost is contained
                      by lazy loading plus rendering a few groups at a time. */}
                  <img
                    src={photo.image_url || photo.thumbnail_url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                  {!editMode && (
                    <button
                      className="pg-tile-download"
                      onClick={(e) => handleDownloadPhoto(e, photo)}
                      aria-label="Download photo"
                      title="Download photo"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M10 12.5a.75.75 0 0 0 .53-.22l3.5-3.5a.75.75 0 0 0-1.06-1.06L10.75 9.94V3a.75.75 0 0 0-1.5 0v6.94L7.03 7.72a.75.75 0 0 0-1.06 1.06l3.5 3.5c.14.14.33.22.53.22ZM4.75 13a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 6 18.25h8A2.75 2.75 0 0 0 16.75 15.5V13a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H6c-.69 0-1.25-.56-1.25-1.25V13Z"/></svg>
                    </button>
                  )}
                  {editMode && (
                    <span className="pg-tile-num">{editOrder.indexOf(photo.id) + 1}</span>
                  )}
                  {editMode && selectedTileId === photo.id && (
                    <span className="pg-tile-check">✓</span>
                  )}
                </div>
              ))}
            </div>
            <div className="pg-words">
              <span className="pg-words-mark">{theme.mark}</span>
              {editMode ? (
                <textarea
                  className="pg-words-input"
                  value={spread.captions[ci] || ''}
                  onChange={(e) => handleCaptionChange(ci, e.target.value)}
                  maxLength={400}
                  rows={2}
                />
              ) : (
                <p className="pg-words-text">{spread.captions[ci]}</p>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      {hasMoreChunks && (
        <div className="pg-more">
          <div ref={sentinelRef} className="pg-more-sentinel" aria-hidden="true" />
          <button className="pg-more-btn" onClick={measureMore}>
            Show more photos
          </button>
        </div>
      )}
      </>
      )}

      {/* Footer */}
      <footer className="pg-footer">
        <p className="pg-footer-text">{pluralize(photos.length, 'photo')} · curated by Photentra AI</p>
        <Link to={`/shared/${token}/gallery`} className="pg-footer-link">View the full event gallery →</Link>
      </footer>


      {/* Sticky save/cancel bar while editing */}
      {editMode && (
        <div className="pg-edit-bar">
          <span className="pg-edit-bar-label">Customizing this gallery</span>
          <div className="pg-edit-bar-actions">
            <button className="pg-btn" onClick={cancelEdit} disabled={saving}>Cancel</button>
            <button className="pg-btn pg-btn--primary" onClick={handleSaveLayout} disabled={saving}>
              {saving ? 'Saving…' : 'Save Layout'}
            </button>
          </div>
        </div>
      )}

      {toast && <div className="pg-toast">{toast}</div>}
    </div>
  );
}

export default PersonGallery;
